import { useEffect, useRef } from 'react';
import { Modal, Text, Badge, Button, Stack, Group, Loader, Alert, Anchor } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconCheck, IconX, IconClock, IconInfoCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';

interface CollectorProgress {
  name: string;
  status: 'pending' | 'running' | 'complete' | 'failed';
  records_collected?: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
}

interface RefreshJob {
  job_id: string;
  time_period: string;
  status: string;
  collectors: CollectorProgress[];
  started_at: string;
  completed_at?: string;
}

interface RefreshProgressModalProps {
  jobId: string | null;
  opened: boolean;
  onClose: () => void;
  onRefreshComplete?: () => void;
  timePeriod: 'today' | 'week' | 'month';
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending': return <IconClock size={16} color="gray" />;
    case 'running': return <Loader size={16} />;
    case 'complete': return <IconCheck size={16} color="green" />;
    case 'failed': return <IconX size={16} color="red" />;
    default: return null;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'gray';
    case 'running': return 'blue';
    case 'complete': return 'green';
    case 'failed': return 'red';
    default: return 'gray';
  }
};

export function RefreshProgressModal({ jobId, opened, onClose, onRefreshComplete, timePeriod }: RefreshProgressModalProps) {
  const { data: status } = useQuery<RefreshJob>({
    queryKey: ['refresh-status', jobId],
    queryFn: () => api.get<RefreshJob>(`/api/refresh/status/${jobId}`),
    refetchInterval: 2000, // Poll every 2 seconds
    enabled: opened && !!jobId
  });

  const allDone = status?.status === 'complete' || status?.status === 'failed';

  const hasFiredComplete = useRef(false);

  useEffect(() => {
    if (allDone && onRefreshComplete && !hasFiredComplete.current) {
      hasFiredComplete.current = true;
      onRefreshComplete();
    }
  }, [allDone, onRefreshComplete]);

  // Reset the ref when a new job starts
  useEffect(() => {
    if (jobId) {
      hasFiredComplete.current = false;
    }
  }, [jobId]);

  const estimates = {
    today: '1-2 min',
    week: '2-5 min',
    month: '15-30 min'
  };

  return (
    <Modal
      opened={opened}
      onClose={allDone ? onClose : () => {}}
      title="Refreshing Data"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={allDone}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Estimated: {estimates[timePeriod]}
        </Text>

        {status?.collectors.map((collector) => (
          <Stack key={collector.name} gap="xs">
            <Group justify="space-between">
              <Group gap="xs">
                {getStatusIcon(collector.status)}
                <Text fw={500}>{collector.name}</Text>
                <Badge size="sm" color={getStatusColor(collector.status)}>
                  {collector.status}
                </Badge>
              </Group>
            </Group>

            {collector.status === 'complete' && (
              <Text size="sm" c="dimmed">
                {collector.records_collected} records found ({(collector.duration_ms! / 1000).toFixed(1)}s)
              </Text>
            )}

            {collector.status === 'failed' && collector.error_message && (
              <Text size="sm" c="red">
                {collector.error_message}
              </Text>
            )}
          </Stack>
        ))}

        <Alert color="blue" icon={<IconInfoCircle size={16} />} mt="md">
          <Text size="sm">
            To add more data sources or configure additional devices for your tank,{' '}
            <Anchor component={Link} to="/devices">visit the Devices tab</Anchor>.
          </Text>
        </Alert>

        {allDone && (
          <Button onClick={onClose} mt="md" fullWidth>
            Close
          </Button>
        )}
      </Stack>
    </Modal>
  );
}
